import React, { useState } from 'react';
import {
  LEADS_TEST_MODE, WA_AGENT_ENABLED, LEAD_STATUSES, LEAD_CHANNELS, OPEN_STATUSES, LEAD_SUPERVISOR_ROLES, followupState, stockLabel, rpcError, possiblyAttended
} from './config';
import { L, Chips } from './ui';
import LeadCard from './LeadCard';
import AssignControl from './AssignControl';
import DiscardControl from './DiscardControl';
import Metrics from './Metrics';
import CrmConnection from './CrmConnection';
import WhatsAppAgentSettings from './WhatsAppAgentSettings';

// Estado inicial de los filtros del panel (se guarda en LeadsModal para no perderlo al abrir un lead).
export const DEFAULT_PANEL = {
  sub: 'todos', status: 'abiertos', owner: '', channel: '', q: '', from: '', to: '', includeTest: LEADS_TEST_MODE, sort: 'antiguos'
};

const ROLE_NAMES = { creador: 'Creador', responsable: 'Responsable', comercial: 'Comercial', captador: 'Captador' };

// Panel de supervisión (solo creador y responsable): todos los leads y equipo/accesos.
export default function Supervision({ supabase, leads, profiles, accessRows, vehicles, columns, afterChange, onOpen, state, setState }) {
  const set = (patch) => setState(s => ({ ...s, ...patch }));
  return (
    <div style={{ display: 'grid', gap: '12px', minWidth: 0 }}>
      <Chips small value={state.sub} onChange={sub => set({ sub })} options={[
        { key: 'todos', label: '📋 Todos los leads' },
        { key: 'metricas', label: '📊 Métricas' },
        { key: 'equipo', label: '👥 Equipo y accesos' },
        { key: 'crm', label: '🔌 Conexión CRM' },
        ...(WA_AGENT_ENABLED ? [{ key: 'whatsapp', label: '🤖 Agente WhatsApp' }] : [])
      ]} />
      {state.sub === 'metricas' ? (
        <Metrics supabase={supabase} refreshKey={`${leads.length}-${leads.reduce((m, l) => (l.updated_at > m ? l.updated_at : m), '')}`} />
      ) : state.sub === 'crm' ? (
        <CrmConnection supabase={supabase} />
      ) : WA_AGENT_ENABLED && state.sub === 'whatsapp' ? (
        <WhatsAppAgentSettings supabase={supabase} />
      ) : state.sub === 'equipo'
        ? <Team supabase={supabase} leads={leads} profiles={profiles} accessRows={accessRows} afterChange={afterChange} />
        : <AllLeads supabase={supabase} leads={leads} profiles={profiles} accessRows={accessRows} vehicles={vehicles}
            columns={columns} afterChange={afterChange} onOpen={onOpen} state={state} set={set} />}
    </div>
  );
}

// ---------------- Todos los leads ----------------

function AllLeads({ supabase, leads, profiles, accessRows, vehicles, columns, afterChange, onOpen, state, set }) {
  const [busy, setBusy] = useState(null);
  const [errors, setErrors] = useState({});
  const nameOf = (id) => (profiles.find(p => p.id === id)?.full_name || '').trim() || 'Usuario';
  const owners = [...new Set(leads.map(l => l.assigned_to).filter(Boolean))]
    .map(id => ({ id, name: nameOf(id) })).sort((a, b) => a.name.localeCompare(b.name, 'es'));

  const q = state.q.trim().toLowerCase();
  const fromTs = state.from ? new Date(`${state.from}T00:00:00`).getTime() : null;
  const toTs = state.to ? new Date(`${state.to}T23:59:59`).getTime() : null;
  const list = leads
    .filter(l => state.includeTest || !l.is_test)
    .filter(l => state.status === 'todos' ? true
      : state.status === 'abiertos' ? OPEN_STATUSES.includes(l.status)
        : state.status === 'cerrados' ? !OPEN_STATUSES.includes(l.status)
          : state.status === 'vencidos' ? followupState(l).key === 'late'
            : l.status === state.status)
    .filter(l => !state.owner || (state.owner === 'none' ? !l.assigned_to : l.assigned_to === state.owner))
    .filter(l => !state.channel || l.channel === state.channel)
    .filter(l => !q || [l.customer_name, l.phone, l.email, l.vehicle_text, l.vehicle_plate, l.crm_lead_id]
      .some(v => (v || '').toLowerCase().includes(q)))
    .filter(l => {
      const t = new Date(l.received_at).getTime();
      return (fromTs == null || t >= fromTs) && (toTs == null || t <= toTs);
    })
    .sort((a, b) => {
      if (state.sort === 'recientes') return new Date(b.received_at) - new Date(a.received_at);
      if (state.sort === 'seguimiento') {
        const rank = (l) => ({ late: 0, today: 1, ok: 2, none: 3 }[followupState(l).key]);
        return (rank(a) - rank(b)) || String(a.next_followup || '9').localeCompare(String(b.next_followup || '9'));
      }
      return new Date(a.received_at) - new Date(b.received_at);
    });

  const unassign = async (lead) => {
    setBusy(lead.id); setErrors(e => ({ ...e, [lead.id]: '' }));
    const { error } = await supabase.rpc('unassign_lead', { p_lead_id: lead.id });
    setBusy(null);
    if (error) { setErrors(e => ({ ...e, [lead.id]: rpcError(error) })); return; }
    afterChange();
  };

  const switchChannel = async (lead, channel) => {
    if (!channel || channel === lead.channel) return;
    setBusy(`ch-${lead.id}`); setErrors(e => ({ ...e, [lead.id]: '' }));
    const { error } = await supabase.rpc('set_lead_channel', { p_lead_id: lead.id, p_channel: channel });
    setBusy(null);
    if (error) { setErrors(e => ({ ...e, [lead.id]: rpcError(error) })); return; }
    afterChange();
  };

  const visible = leads.filter(l => state.includeTest || !l.is_test);
  const counts = {
    sinAsignar: visible.filter(l => !l.assigned_to && OPEN_STATUSES.includes(l.status)).length,
    sinContacto: visible.filter(l => l.assigned_to && !l.first_contact_at && OPEN_STATUSES.includes(l.status)).length,
    vencidos: visible.filter(l => followupState(l).key === 'late').length,
    abiertos: visible.filter(l => OPEN_STATUSES.includes(l.status)).length
  };
  const filtersOn = state.status !== 'abiertos' || state.owner || state.channel || state.q || state.from || state.to;

  return (
    <div style={{ display: 'grid', gap: '10px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        <Counter n={counts.sinAsignar} label="sin asignar" color="var(--t-f87171)" onClick={() => set({ status: 'abiertos', owner: 'none' })} />
        <Counter n={counts.vencidos} label="seguimientos vencidos" color="var(--t-f87171)" onClick={() => set({ status: 'vencidos', owner: '' })} />
        <Counter n={counts.abiertos} label="abiertos" color="var(--t-93c5fd)" onClick={() => set({ status: 'abiertos', owner: '' })} />
      </div>

      <div style={{ ...L.section, marginBottom: 0, display: 'grid', gap: '8px' }}>
        <input aria-label="Buscar leads" placeholder="Buscar por nombre, teléfono, email, coche, matrícula o referencia…"
          value={state.q} onChange={e => set({ q: e.target.value })} style={{ ...L.input, marginBottom: 0 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
          <select aria-label="Estado" value={state.status} onChange={e => set({ status: e.target.value })} style={{ ...L.input, marginBottom: 0 }}>
            <option value="abiertos">Abiertos</option>
            <option value="vencidos">Seguimiento vencido</option>
            <option value="cerrados">Cerrados</option>
            <option value="todos">Todos los estados</option>
            {LEAD_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select aria-label="Asignado a" value={state.owner} onChange={e => set({ owner: e.target.value })} style={{ ...L.input, marginBottom: 0 }}>
            <option value="">Cualquier comercial</option>
            <option value="none">Sin asignar</option>
            {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <select aria-label="Canal" value={state.channel || ''} onChange={e => set({ channel: e.target.value })} style={{ ...L.input, marginBottom: 0 }}>
            <option value="">Todos los canales</option>
            {LEAD_CHANNELS.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
          </select>
          <select aria-label="Orden" value={state.sort} onChange={e => set({ sort: e.target.value })} style={{ ...L.input, marginBottom: 0 }}>
            <option value="antiguos">Más antiguos primero</option>
            <option value="recientes">Más recientes primero</option>
            <option value="seguimiento">Seguimiento más urgente</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', alignItems: 'center' }}>
          <label style={{ ...L.muted, display: 'flex', alignItems: 'center', gap: '6px' }}>
            Entrada desde
            <input type="date" value={state.from} onChange={e => set({ from: e.target.value })} style={{ ...L.input, marginBottom: 0, width: 'auto', padding: '5px 8px' }} />
          </label>
          <label style={{ ...L.muted, display: 'flex', alignItems: 'center', gap: '6px' }}>
            hasta
            <input type="date" value={state.to} onChange={e => set({ to: e.target.value })} style={{ ...L.input, marginBottom: 0, width: 'auto', padding: '5px 8px' }} />
          </label>
          {LEADS_TEST_MODE && (
            <label style={{ ...L.muted, display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" checked={state.includeTest} onChange={e => set({ includeTest: e.target.checked })} />
              Incluir leads de prueba
            </label>
          )}
          {filtersOn && (
            <button type="button" style={{ ...L.secondary, padding: '4px 10px', fontSize: '0.8rem' }}
              onClick={() => set({ status: 'abiertos', owner: '', channel: '', q: '', from: '', to: '' })}>Quitar filtros</button>
          )}
        </div>
      </div>

      <p style={{ ...L.muted, margin: 0 }}>{list.length} lead{list.length === 1 ? '' : 's'}</p>
      {list.length === 0 && <p style={L.muted}>Ningún lead coincide con los filtros.</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))', gap: '10px' }}>
        {list.map(lead => {
          const open = OPEN_STATUSES.includes(lead.status);
          return (
            <div key={lead.id}>
              <LeadCard
                lead={lead}
                mode={lead.assigned_to ? 'cartera' : 'bandeja'}
                stock={stockLabel(lead, vehicles, columns)}
                ownerName={lead.assigned_to ? nameOf(lead.assigned_to) : 'Sin asignar'}
                onOpen={() => onOpen(lead.id)}
                actions={(
                  <>
                    {open && <>
                    <AssignControl compact supabase={supabase} lead={lead} profiles={profiles} accessRows={accessRows} onDone={afterChange} />
                    {lead.assigned_to && (
                      <button type="button" style={{ ...L.secondary, padding: '6px 10px', fontSize: '0.8rem' }} disabled={busy === lead.id} onClick={() => unassign(lead)}>
                        📥 A la Bandeja
                      </button>
                    )}
                    {possiblyAttended(lead) && <DiscardControl compact supabase={supabase} lead={lead} onDone={afterChange} />}
                    </>}
                    <select aria-label="Cambiar el canal del lead" title="Cambiar el canal del lead (cuenta aparte en las métricas)"
                      value={lead.channel} disabled={busy === `ch-${lead.id}`} onChange={e => switchChannel(lead, e.target.value)}
                      style={{ ...L.input, marginBottom: 0, width: 'auto', padding: '5px 8px', fontSize: '0.8rem' }}>
                      {LEAD_CHANNELS.map(c => <option key={c.key} value={c.key}>🔁 {c.icon} {c.label}</option>)}
                    </select>
                  </>
                )}
              />
              {errors[lead.id] && <div style={{ ...L.error, marginTop: '6px' }}>{errors[lead.id]}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Counter({ n, label, color, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick}
      style={{ backgroundColor: 'var(--b-0f172a)', border: '1px solid var(--l-334155)', borderRadius: '8px', padding: '6px 10px',
        display: 'flex', alignItems: 'baseline', gap: '6px', cursor: onClick ? 'pointer' : 'default', font: 'inherit' }}>
      <strong style={{ color: n > 0 ? color : 'var(--t-6b7280)', fontSize: '1.05rem' }}>{n}</strong>
      <span style={{ color: 'var(--t-9ca3af)', fontSize: '0.78rem' }}>{label}</span>
    </Tag>
  );
}

// ---------------- Equipo y accesos ----------------

function Team({ supabase, leads, profiles, accessRows, afterChange }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const accessOf = (id) => accessRows.find(a => a.profile_id === id) || { enabled: false, can_claim: false };
  const stats = (id) => {
    const mine = leads.filter(l => l.assigned_to === id);
    return {
      abiertos: mine.filter(l => OPEN_STATUSES.includes(l.status)).length,
      atendidos: mine.filter(l => l.first_contact_at).length,
      sinContacto: mine.filter(l => OPEN_STATUSES.includes(l.status) && !l.first_contact_at).length
    };
  };

  const toggle = async (p, field) => {
    const a = accessOf(p.id);
    const enabled = field === 'enabled' ? !a.enabled : a.enabled;
    // Al activar el panel por primera vez, "puede coger" toma el valor por defecto del rol (comercial sí, captador no).
    const canClaim = field === 'can_claim' ? !a.can_claim : (accessRows.some(r => r.profile_id === p.id) ? a.can_claim : null);
    setBusy(`${p.id}-${field}`); setError('');
    const { error: err } = await supabase.rpc('set_lead_access', { p_profile_id: p.id, p_enabled: enabled, p_can_claim: canClaim });
    setBusy(null);
    if (err) { setError(rpcError(err)); return; }
    afterChange();
  };

  const order = { creador: 0, responsable: 1, comercial: 2, captador: 3 };
  const people = [...profiles].sort((a, b) =>
    ((order[a.role] ?? 9) - (order[b.role] ?? 9)) || (a.full_name || '').localeCompare(b.full_name || '', 'es'));

  return (
    <div style={{ display: 'grid', gap: '10px' }}>
      <div style={L.warn}>
        Los usuarios con el panel desactivado <strong>no ven el módulo ni reciben avisos de leads</strong>. "Puede coger" deja coger leads de la
        Bandeja y recibir el aviso de lead nuevo; sin él, solo ven los leads que les asignes. Los cambios se aplican al momento.
      </div>
      {error && <div style={L.error}>{error}</div>}
      <div style={{ display: 'grid', gap: '8px' }}>
        {people.map(p => {
          const supervisor = LEAD_SUPERVISOR_ROLES.includes(p.role);
          const manageable = ['comercial', 'captador'].includes(p.role);
          const a = accessOf(p.id);
          const s = stats(p.id);
          return (
            <div key={p.id} style={{ ...L.card, opacity: supervisor || manageable ? 1 : 0.5, gridTemplateColumns: '1fr', gap: '8px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 12px' }}>
                <strong style={{ color: 'var(--t-f1f5f9)' }}>{(p.full_name || '').trim() || 'Sin nombre'}</strong>
                <span style={L.muted}>{ROLE_NAMES[p.role] || p.role}</span>
                {(supervisor || manageable) && (
                  <span style={{ ...L.muted, marginLeft: 'auto' }}>
                    {s.abiertos} abiertos · {s.atendidos} atendidos
                  </span>
                )}
              </div>
              {supervisor ? (
                <span style={{ color: 'var(--t-86efac)', fontSize: '0.8rem' }}>✅ Acceso permanente (supervisa todos los leads)</span>
              ) : manageable ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                  <Toggle on={a.enabled} busy={busy === `${p.id}-enabled`} onClick={() => toggle(p, 'enabled')}
                    label="Panel de leads" />
                  <Toggle on={a.enabled && a.can_claim} disabled={!a.enabled} busy={busy === `${p.id}-can_claim`} onClick={() => toggle(p, 'can_claim')}
                    label="Puede coger de la Bandeja" />
                  {!a.enabled && s.abiertos > 0 && (
                    <span style={{ color: 'var(--t-fbbf24)', fontSize: '0.78rem' }}>⚠️ Tiene {s.abiertos} lead{s.abiertos === 1 ? '' : 's'} abierto{s.abiertos === 1 ? '' : 's'} que ya no ve: reasígnalos.</span>
                  )}
                </div>
              ) : (
                <span style={L.muted}>Este rol no atiende leads.</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Toggle({ on, label, onClick, disabled, busy }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} disabled={disabled || busy} onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '8px', borderRadius: '999px', padding: '5px 12px 5px 6px',
        border: `1px solid ${on ? 'var(--l-166534)' : 'var(--l-4b5563)'}`,
        backgroundColor: on ? 'var(--b-14532d)' : 'transparent', color: on ? 'var(--t-86efac)' : 'var(--t-9ca3af)',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, fontSize: '0.82rem'
      }}>
      <span aria-hidden="true" style={{
        width: '28px', height: '16px', borderRadius: '999px', position: 'relative', flexShrink: 0,
        backgroundColor: on ? '#16a34a' : '#9ca3af'
      }}>
        <span style={{ position: 'absolute', top: '2px', left: on ? '14px' : '2px', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#ffffff', transition: 'left 0.15s' }} />
      </span>
      {busy ? 'Guardando…' : label}
    </button>
  );
}
